/*
 *   File : check-status.js
 *   Author : https://evoqins.com
 *   Description : check-status modal component
 *   Integrations : NA
 *   Version : 1.0.0
*/

//Custom components
import { Icon } from '../Icon';
import { PrimaryButton, SecondaryButton } from '../Buttons';

const CheckStatusModal = (props) => {
    const MAX_RENDER_DEPTH = 8;

    const _humanizeKey = (key) => {
        if (!key) return '';
        const str = String(key)
            .replace(/_/g, ' ')
            .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
            .replace(/\s+/g, ' ')
            .trim();

        return str
            .split(' ')
            .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
            .join(' ');
    };

    const _normalizeData = (value) => {
        if (value == null) return null;
        if (typeof value === 'string') {
            try {
                return JSON.parse(value);
            } catch (e) {
                return value;
            }
        }
        return value;
    };

    const _hasRenderableData = (value) => {
        if (value == null) return false;
        if (typeof value === 'string') return value.trim().length > 0;
        if (Array.isArray(value)) return value.length > 0;
        if (typeof value === 'object') return Object.keys(value).length > 0;
        return true;
    };

    const _formatDateTime = (value) => {
        if (typeof value !== 'string') return null;
        if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)) return null;
        const ms = Date.parse(value);
        if (Number.isNaN(ms)) return null;
        try {
            return new Date(ms).toLocaleString(undefined, {
                year: 'numeric',
                month: 'short',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
            });
        } catch (e) {
            return new Date(ms).toString();
        }
    };

    const _formatPrimitive = (value) => {
        if (value === null) return 'null';
        if (value === undefined) return '—';
        if (typeof value === 'boolean') return value ? 'true' : 'false';
        if (typeof value === 'number') return String(value);
        if (typeof value === 'string') return _formatDateTime(value) ?? value;
        return String(value);
    };

    const _renderKeyValue = (label, value, key, depth = 0) => (
        <div key={key} className='d-flex align-items-start gap-2'>
            <span
                className='e-text-dark-gray e-primary-font e-font-14 e-font-weight-600 line-height-20px'
                style={{ flex: '0 0 180px', maxWidth: 180, paddingLeft: depth * 14 }}
            >
                {_humanizeKey(label)}:
            </span>
            <span className='e-text-arsenic e-primary-font e-font-14 e-font-weight-400 line-height-20px' style={{ wordBreak: 'break-word' }}>
                {_formatPrimitive(value)}
            </span>
        </div>
    );

    const _renderObject = (obj, depth = 0, parentKey = 'root', seen = new WeakSet()) => {
        if (obj == null) return null;
        if (typeof obj !== 'object') return _renderKeyValue(parentKey, obj, `${parentKey}-primitive`, depth);

        if (depth > MAX_RENDER_DEPTH) {
            return _renderKeyValue('Note', '[Max depth reached]', `${parentKey}-max-depth`, depth);
        }

        if (seen.has(obj)) {
            return _renderKeyValue('Note', '[Circular]', `${parentKey}-circular`, depth);
        }
        seen.add(obj);

        const entries = Object.entries(obj);
        if (entries.length === 0) return null;

        return (
            <div className='d-flex flex-column gap-'>
                {entries.map(([key, value]) => {
                    const blockClassName = depth === 0 ? 'e-border-bottom-1px-platinum-light p-2 mb-' : '';
                    if (Array.isArray(value)) {
                        return (
                            <div key={`${parentKey}-${key}`} className={`d-flex flex-column gap-2 ${blockClassName}`}>
                                <div
                                    className='e-text-chinese-black e-primary-font e-font-16 e-font-weight-600 line-height-24px'
                                    style={{ paddingLeft: depth * 14 }}
                                >
                                    {_humanizeKey(key)}
                                </div>
                                <div className='d-flex flex-column gap-2'>
                                    {value.length === 0 ? (
                                        <div className='e-text-arsenic e-primary-font e-font-14 e-font-weight-400 line-height-20px' style={{ paddingLeft: (depth + 1) * 14 }}>—</div>
                                    ) : (
                                        value.map((item, idx) => {
                                            const itemKey = `${parentKey}-${key}-${idx}`;
                                            if (item != null && typeof item === 'object') {
                                                return (
                                                    <div key={itemKey} className='e-border-1px-platinum-light border-radius-12 padding-12px'>
                                                        <div
                                                            className='e-text-dark-gray e-primary-font e-font-14 e-font-weight-600 line-height-20px mb-2'
                                                            style={{ paddingLeft: (depth + 1) * 14 }}
                                                        >
                                                            {_humanizeKey(key)} #{idx + 1}
                                                        </div>
                                                        {_renderObject(item, depth + 1, itemKey, seen)}
                                                    </div>
                                                );
                                            }
                                            return (
                                                <div
                                                    key={itemKey}
                                                    className='e-text-arsenic e-primary-font e-font-14 e-font-weight-400 line-height-20px'
                                                    style={{ wordBreak: 'break-word', paddingLeft: (depth + 1) * 14 }}
                                                >
                                                    {_formatPrimitive(item)}
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            </div>
                        );
                    }

                    if (value != null && typeof value === 'object') {
                        return (
                            <div key={`${parentKey}-${key}`} className={`d-flex flex-column gap-2 ${blockClassName}`}>
                                <div
                                    className='e-text-chinese-black e-primary-font e-font-16 e-font-weight-600 line-height-24px'
                                    style={{ paddingLeft: depth * 14 }}
                                >
                                    {_humanizeKey(key)}
                                </div>
                                {_renderObject(value, depth + 1, `${parentKey}-${key}`, seen)}
                            </div>
                        );
                    }

                    if (depth === 0) {
                        return (
                            <div key={`${parentKey}-${key}`} className={blockClassName}>
                                {_renderKeyValue(key, value, `${parentKey}-${key}`, depth)}
                            </div>
                        );
                    }

                    return _renderKeyValue(key, value, `${parentKey}-${key}`, depth);
                })}
            </div>
        );
    };

    // handle close modal
    const _handleCloseModal = () => {
        var button = document.getElementById("check-status-modal-close");
        if (button && typeof button.click === 'function') button.click();
    };

    // handle success 
    const _handleSuccess = () => {
        props.handleSuccess();
        _handleCloseModal();
    };

    return (
        <div className="modal fade"
            id="check-status-modal"
            data-bs-backdrop="static"
            data-bs-keyboard="false"
            tabIndex="-1"
            aria-labelledby="modalLabel"
            aria-hidden="true">

            <div className={`modal-lg modal-dialog modal-dialog-centered modal-dialog-scrollable border-radius-28 e-check-status-modal `}>
                <span id='check-status-modal-close' className='d-none' data-bs-dismiss="modal"></span>
                <div className="modal-content border-radius-28 overflow-hidden border-0 e-bg-white-dark pb-4">

                    {/* modal header */}
                    <div className="modal-header border-0 justify-content-between align-items-center padding-24px-lr padding-24px-top pb-4">
                        <h6 className='mb-1 e-text-chinese-black e-primary-font e-font-18 e-font-weight-600 line-height-24px'>
                            {props.title??"Customer Status"}
                        </h6>
                        <Icon icon='close'
                            size="1.125rem"
                            className="d-flex cursor-pointer"
                            onClick={_handleSuccess} />
                    </div>

                    {/* Modal body */}
                    <div className='modal-body pt-0 pb-0 padding-24px-lr'>
                        {props.type === 1 ?
                            <>
                                <div className='e-border-bottom-1px-platinum-light pb-1'>
                                    <div className='row'>
                                        <div className='col-4'>
                                            <p className='mb-1 e-text-dark-gray e-primary-font e-font-16 e-font-weight-600 line-height-20px'>
                                                Name:
                                            </p>
                                        </div>

                                        <div className='col-4'>
                                            <p className='mb-1 e-text-dark-gray e-primary-font e-font-16 e-font-weight-600 line-height-20px'>
                                                Message:
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {props.data.map((data, index) => (
                                    <div key={index} className='e-border-bottom-1px-platinum-light padding-12px-tb'>
                                        <div className='row'>
                                            <div className='col-4'>


                                                <p className='mb-0 e-text-arsenic e-primary-font e-font-16 e-font-weight-400 line-height-20px'>
                                                    {data.customer_name}
                                                </p>
                                            </div>
                                            <div className='col-8'>
                                                <p className='mb-0 e-text-arsenic e-primary-font e-font-16 e-font-weight-400 line-height-20px'>
                                                    {data.message}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </>
                            :
                            props.type === 2 ?
                                (() => {
                                    const normalized = _normalizeData(props.data);
                                    if (!_hasRenderableData(normalized)) {
                                        return (
                                            <div className='e-bg-magnolia border-radius-8 p-5'>
                                                <div className='row  '>
                                                    <div className='justify-content-center  align-items-center d-flex e-text-smoky-black e-primary-font e-font-16 e-font-weight-600 line-height-20px'>
                                                        No data found
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    }
                                    return (
                                        <div className='e-border-1px-platinum-light border-radius-12 padding-12px e-bg-white-dark'>
                                            {_renderObject(normalized)}
                                        </div>
                                    );
                                })()
                                :
                                <></>
                        }
                    </div>

                    {/* Modal footer */}
                    <div className='modal-footer border-0 pt-4 padding-24px-lr pb-0 '>
                        <PrimaryButton name="OK"
                            className="passing-12px-tb padding-66px-lr"
                            onClick={_handleSuccess} />
                    </div>
                </div>
            </div>
        </div>
    )
}

export default CheckStatusModal;
